import { useCallback, useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useParams } from 'react-router-dom';
import TermsConditions from 'wtb-components/TermsConditions';
import { Logger } from 'wtb-utils';

import { getMerchantTerms, getTerms } from 'api';
import { useLocaleContext } from 'context';
import { NotFound } from 'pages';

import styles from './Terms.module.css';

const getCommonItems = (first, second) => first.filter((item) => second.includes(item));

const isAvailableLocale = (locale, availableLanguages, getBaseLocale) => (
  availableLanguages.includes(locale)
  || availableLanguages.some((availableLocale) => getBaseLocale(availableLocale) === locale)
);

const Terms = () => {
  const { id } = useParams();
  const {
    locale,
    changeLocale,
    getBaseLocale,
    getBrowserLocaleCandidates,
    isVariantLocale,
    persistLocale
  } = useLocaleContext();
  const [ hasError, setHasError ] = useState(false);
  const [ isLoading, setIsLoading ] = useState(false);
  const [ terms, setTerms ] = useState(null);
  const isMerchant = Boolean(id);

  const manageError = useCallback((error) => {
    Logger.error(error);
    Sentry.captureException(error);
    setHasError(true);
  }, []);

  useEffect(() => {
    if (!locale) {
      return undefined;
    }

    let isActive = true;
    const termsRequest = isMerchant ? getMerchantTerms(id) : getTerms();

    setHasError(false);
    setIsLoading(true);
    setTerms(null);

    termsRequest
      .then((response) => {
        if (!isActive) {
          return;
        }

        let availableLanguages = [];

        response.forEach((section, index) => {
          const titleLanguages = Object.keys(section.title || {});
          const contentLanguages = Object.keys(section.content || {});

          if (!section.title || !section.content) {
            Logger.error(`Section index ${index} has empty title or content. Please check the format.`);
          }

          const commonLanguages = getCommonItems(titleLanguages, contentLanguages);
          availableLanguages = availableLanguages.length
            ? getCommonItems(availableLanguages, commonLanguages)
            : commonLanguages;
        });

        if (isVariantLocale(locale)) {
          if (!availableLanguages.includes(locale)) {
            changeLocale(getBaseLocale(locale), { replace: true });
            return;
          }

          persistLocale(locale);
        } else if (!isAvailableLocale(locale, availableLanguages, getBaseLocale)) {
          const fallbackLanguage = getBrowserLocaleCandidates()
            .find((candidate) => isAvailableLocale(candidate, availableLanguages, getBaseLocale)) || 'en';

          changeLocale(fallbackLanguage, { replace: true });
          return;
        }

        setTerms(response);
      })
      .catch((error) => {
        if (isActive) {
          manageError(error);
        }
      })
      .finally(() => {
        if (isActive) {
          setIsLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [
    changeLocale,
    getBrowserLocaleCandidates,
    getBaseLocale,
    id,
    isMerchant,
    isVariantLocale,
    locale,
    manageError,
    persistLocale
  ]);

  if (hasError) {
    return <NotFound />;
  }

  return (
    <div className={styles.root}>
      {isMerchant && !isLoading && (
        <div className={styles.logoBox}>
          <img
            className={styles.merchant}
            src={`${import.meta.env.VITE_CDN_URL}/merchant/${id}/logo.png`}
            alt="merchant logo"
          />
        </div>
      )}

      {terms && (
        <TermsConditions
          sections={terms}
          className={styles.terms}
          locale={locale}
        />
      )}
    </div>
  );
};

export default Terms;
